export type RequiredDynamicFieldLike = {
  type?: unknown;
  value?: unknown;
  attr_id?: unknown;
  isRemovable?: unknown;
  isRequired?: unknown;
};

export type DynamicAttributeValueLike = {
  attr_id?: unknown;
  attr_name?: unknown;
  attr_value?: unknown;
};

const hasRequiredFieldValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.some(hasRequiredFieldValue);
  }

  if (typeof value === 'object') {
    const item = value as Record<string, unknown>;
    return (
      hasRequiredFieldValue(item.value) ||
      hasRequiredFieldValue(item.label) ||
      hasRequiredFieldValue(item.name)
    );
  }

  return String(value).trim().length > 0;
};

export const missingRequiredDynamicTypes = (
  fields: RequiredDynamicFieldLike[] | null | undefined,
  attributes?: DynamicAttributeValueLike[] | null
): string[] => {
  const source = Array.isArray(fields) ? fields : [];
  const attributeSource = Array.isArray(attributes) ? attributes : [];
  const requiredFields = source.filter((field) => {
    const attrId = Number(field?.attr_id);
    return (
      !field?.isRemovable &&
      Boolean(field?.isRequired) &&
      Number.isFinite(attrId)
    );
  });
  const normalizeName = (value: unknown): string =>
    String(value ?? '').normalize('NFKC').trim();
  const requiredTypes = Array.from(
    new Set(
      requiredFields.map((field) => normalizeName(field?.type)).filter(Boolean)
    )
  );

  return requiredTypes.filter(
    (type) => {
      const requiredAttrIds = new Set(
        requiredFields
          .filter((field) => normalizeName(field?.type) === type)
          .map((field) => Number(field?.attr_id))
          .filter(Number.isFinite)
      );
      const hasFieldValue = source.some(
        (field) =>
          normalizeName(field?.type) === type &&
          hasRequiredFieldValue(field?.value)
      );
      const hasRegisteredAttributeValue = attributeSource.some((attribute) => {
        const attrId = Number(attribute?.attr_id);
        const hasComparableId = Number.isFinite(attrId) && requiredAttrIds.size > 0;
        const matchesId = hasComparableId && requiredAttrIds.has(attrId);
        const matchesName =
          !hasComparableId && normalizeName(attribute?.attr_name) === type;
        return (matchesId || matchesName) && hasRequiredFieldValue(attribute?.attr_value);
      });

      return !hasFieldValue && !hasRegisteredAttributeValue;
    }
  );
};
